/**
 * Request Amendment Modal
 * Modal for requesting amendments to 2D, 3D designs, or signing/quotation
 */

import React, { useState } from 'react';
import { useProjects } from '../../context/ProjectContext';

const RequestAmendmentModal = ({ isOpen, onClose, projectId, designType, onSuccess }) => {
  const { requestAmendment2D, requestAmendment3D, requestSigningAmendment } = useProjects();

  const [amendmentNotes, setAmendmentNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const isThreeD = designType === '3d';
  const isSigning = designType === 'signing';

  const getTitle = () => {
    if (isSigning) return 'Request Signing Amendment';
    if (isThreeD) return 'Request 3D Amendment';
    return 'Request 2D Amendment';
  };
  const title = getTitle();

  // Handle submission
  const handleSubmit = async () => {
    if (!amendmentNotes.trim()) {
      setError('Please provide amendment details');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const amendmentData = {
        reason: amendmentNotes,
        amendmentNotes: amendmentNotes
      };

      if (isSigning) {
        await requestSigningAmendment(projectId, amendmentData);
      } else if (isThreeD) {
        await requestAmendment3D(projectId, amendmentData);
      } else {
        await requestAmendment2D(projectId, amendmentData);
      }

      // Success callback
      if (onSuccess) {
        onSuccess({
          designType,
          amendmentNotes
        });
      }

      handleClose();
    } catch (err) {
      console.error('Error requesting amendment:', err);
      setError(err.message || 'Failed to request amendment');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset and close
  const handleClose = () => {
    setAmendmentNotes('');
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col my-4">
        {/* Header */}
        <div className="bg-orange-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <h2 className="text-xl font-semibold">{title}</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="text-white hover:text-gray-200 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Info box */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-orange-800">
              {isSigning
                ? 'Request changes to the quotation or contract terms. The project will return to the DD phase for client negotiation revisions.'
                : `Request changes to the ${isThreeD ? '3D design' : '2D drawing'}. The project will return to the design team for revisions.`
              }
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Amendment notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amendment Details *
            </label>
            <textarea
              value={amendmentNotes}
              onChange={(e) => setAmendmentNotes(e.target.value)}
              disabled={submitting}
              rows={8}
              placeholder="Describe the changes needed in detail...

Examples:
- Adjust kitchen cabinet layout
- Change living room color scheme
- Modify bedroom furniture placement
- Update material selections"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 font-mono text-sm"
            />
            <p className="text-sm text-gray-500 mt-2">
              Be specific about the changes needed. This helps the design team understand your requirements clearly.
            </p>
          </div>

          {/* Guidelines */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Tips for Clear Amendment Requests
            </h4>
            <ul className="space-y-1 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Be specific about which room or area needs changes</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Include measurements or dimensions if relevant</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Reference specific elements from the current design</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Attach photos or sketches if you have them (use Upload Files button)</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-lg flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !amendmentNotes.trim()}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Submitting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Request Amendment
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestAmendmentModal;
